import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { gql, useQuery } from '@apollo/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { fileUrl } from '../lib/utils';

const COURSES = gql`
  query Courses(
    $page: Int
    $limit: Int!
    $searchValue: String
    $kind: String
    $category: CourseCategory
    $status: String
  ) {
    courses(
      page: $page
      limit: $limit
      searchValue: $searchValue
      kind: $kind
      category: $category
      status: $status
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        currentPage
        totalPages
      }
      totalCount
      nodes {
        id
        title
        description
        thumbnail
        kind
        category
        duration
      }
    }
  }
`;

type Course = {
  id: string;
  title: string;
  description: string;
  thumbnail?: string | null;
  kind: string;
  category: string;
  duration?: string | null;
};

const LIMIT = 9; // 3 columns × 3 rows

const Treatments = () => {
  const [category, setCategory] = useState<'PRODUCT' | 'DEVICE'>('PRODUCT');
  const [page, setPage] = useState(1);
  const observerRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, fetchMore } = useQuery(COURSES, {
    variables: {
      limit: LIMIT,
      page: 1,
      status: 'active',
      kind: 'training',
      category: category === 'PRODUCT' ? 'PRODUCT' : 'DEVICE',
      // or use `category` field if your backend uses that instead
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const courses: Course[] = data?.courses?.nodes || [];
  const pageInfo = data?.courses?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage;

  // Reset page when category changes
  useEffect(() => {
    setPage(1);
  }, [category]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasNextPage || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, loading, page]);

  const loadMore = () => {
    if (!hasNextPage || loading) return;

    fetchMore({
      variables: {
        page: page + 1,
        limit: LIMIT,
        kind: 'training',
        category,
        status: 'active',
      },
      
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          courses: {
            ...fetchMoreResult.courses,
            nodes: [...prev.courses.nodes, ...fetchMoreResult.courses.nodes],
          },
        };
      },
    }).then(() => {
      setPage((p) => p + 1);
    });
  };

  const handleTabChange = (value: string) => {
    setCategory(value as 'PRODUCT' | 'DEVICE');
    setPage(1);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">Алдаа гарлаа: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold mb-4">
              Гарын авлага, заавар зөвлөгөө
            </h1>
          </div>

          {/* <Tabs value={category} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="PRODUCT">Бүтээгдэхүүн</TabsTrigger>
              <TabsTrigger value="DEVICE">Багаж хэрэгсэл</TabsTrigger>
            </TabsList>

            <TabsContent value={category} className="mt-8"> */}
              {courses.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground text-lg">
                    Одоогоор гарын авлага байхгүй байна.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {courses.map((treatment) => (
                    <Link key={treatment.id} to={`/knowledge/${treatment.id}`}>
                      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group h-full">
                        <div className="relative overflow-hidden aspect-[4/3] bg-gray-100">
                          {treatment.thumbnail ? (
                            <img
                              src={fileUrl(treatment.thumbnail)}
                              alt={treatment.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                              <span className="text-gray-400">Зураг байхгүй</span>
                            </div>
                          )}
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                            {treatment.title}
                          </h3>
                          {/* <p className="text-sm text-primary font-medium mb-3">
                            {treatment.category === 'PRODUCT' ? 'Бүтээгдэхүүн' : 'Багаж'}
                          </p> */}
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-3" dangerouslySetInnerHTML={{ __html: treatment.description }} />
                          {treatment.duration && (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-semibold">Үргэлжлэх хугацаа:</span>{' '}
                              {treatment.duration} мин
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}

              {/* Loading more spinner */}
              {loading && page > 1 && (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {/* Load More Trigger */}
              {hasNextPage && (
                <div ref={observerRef} className="flex justify-center py-12">
                  <Button
                    onClick={loadMore}
                    disabled={loading}
                    size="lg"
                    variant="outline"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Ачаалж байна...
                      </>
                    ) : (
                      'Үргэлжлүүлэх'
                    )}
                  </Button>
                </div>
              )}
            {/* </TabsContent>
          </Tabs> */}
        </div>
      </div>
    </div>
  );
};

export default Treatments;